/**
 * Reusable 3-per-row sliding carousel for featured products (hotels, excursions, etc.).
 * Same layout and animations everywhere: viewport + track, left/right arrow gutters.
 */
import { useMemo, useState, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';

const CARDS_PER_PAGE = 3;
const TRACK_TRANSITION = 'transform 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94)';

/**
 * @param {Object} props
 * @param {Array} props.items - List of items to show (hotels, excursions, etc.)
 * @param {number} [props.itemsPerPage=3] - Items per page/row
 * @param {function} props.renderCard - (item, index) => ReactNode
 * @param {string} [props.prevAriaLabel='Previous']
 * @param {string} [props.nextAriaLabel='Next']
 * @param {string} [props.blockClass='products-carousel'] - BEM block for layout/arrows
 */
const ProductsCarousel = ({
  items = [],
  itemsPerPage = CARDS_PER_PAGE,
  renderCard,
  prevAriaLabel = 'Previous',
  nextAriaLabel = 'Next',
  blockClass = 'products-carousel'
}) => {
  const pages = useMemo(() => {
    const p = [];
    for (let i = 0; i < items.length; i += itemsPerPage) {
      p.push(items.slice(i, i + itemsPerPage));
    }
    return p;
  }, [items, itemsPerPage]);

  const pageCount = pages.length;
  const [pageIndex, setPageIndex] = useState(0);
  const hasNext = pageIndex < pageCount - 1;
  const hasPrev = pageIndex > 0;

  const goNext = useCallback(() => {
    if (pageIndex >= pageCount - 1) return;
    setPageIndex((p) => Math.min(p + 1, pageCount - 1));
  }, [pageIndex, pageCount]);

  const goPrev = useCallback(() => {
    if (pageIndex <= 0) return;
    setPageIndex((p) => Math.max(p - 1, 0));
  }, [pageIndex]);

  if (items.length === 0) return null;

  const wrapClass = `${blockClass}__wrap`;
  const viewportClass = `${blockClass}__viewport`;
  const trackClass = `${blockClass}__track`;
  const pageClass = `${blockClass}__page`;
  const rowClass = `${blockClass}__row`;
  const colClass = `${blockClass}__col`;
  const gutterClass = `${blockClass}__arrow-gutter`;
  const gutterLeftClass = `${blockClass}__arrow-gutter--left`;
  const gutterRightClass = `${blockClass}__arrow-gutter--right`;
  const arrowClass = `${blockClass}__arrow`;
  const arrowLeftClass = `${blockClass}__arrow--left`;

  return (
    <div className={blockClass}>
      <div className={wrapClass}>
        {hasPrev && (
          <div className={`${gutterClass} ${gutterLeftClass}`}>
            <button
              type="button"
              className={`${arrowClass} ${arrowLeftClass}`}
              onClick={goPrev}
              aria-label={prevAriaLabel}
            >
              <BsArrowLeft size={28} />
            </button>
          </div>
        )}
        <div className={viewportClass}>
          <div
            className={trackClass}
            style={{
              width: '100%',
              transform: 'none',
              transition: TRACK_TRANSITION
            }}
          >
            <div className={pageClass} style={{ flex: '0 0 100%' }}>
              <Row className={rowClass}>
                {(pages[pageIndex] || []).map((item, i) => (
                  <Col
                    key={item?.id ?? `${pageIndex}-${i}`}
                    xs={12}
                    md={6}
                    lg={4}
                    className={colClass}
                  >
                    {renderCard(item, pageIndex * itemsPerPage + i)}
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        </div>
        {hasNext && (
          <div className={`${gutterClass} ${gutterRightClass}`}>
            <button
              type="button"
              className={arrowClass}
              onClick={goNext}
              aria-label={nextAriaLabel}
            >
              <BsArrowRight size={28} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsCarousel;
